/**
 * Access-token lifecycle rules, shared so the client's interceptor and the
 * server's guard agree on exactly when a token is considered spent.
 *
 * SPEC: 60-minute access token, 50-day refresh token, and a pre-emptive
 * refresh once the access token is within 5 minutes of expiring.
 */

export const REFRESH_SKEW_SECONDS = 5 * 60;

export interface JwtClaims {
  sub: string;
  email: string;
  tier: string;
  /** Seconds since epoch. */
  exp: number;
  iat: number;
}

/**
 * This module is compiled for the browser, for Node, and on its own with
 * neither set of ambient globals available. Both decoders are therefore
 * reached through `globalThis` with structural types rather than named
 * directly, which would pin the package to one runtime's type definitions.
 */
interface Base64Globals {
  atob?: (input: string) => string;
  Buffer?: {
    from(input: string, encoding: string): { toString(encoding: string): string };
  };
}

function base64Decode(value: string): string {
  const runtime = globalThis as Base64Globals;
  if (typeof runtime.atob === 'function') return runtime.atob(value);
  if (runtime.Buffer) return runtime.Buffer.from(value, 'base64').toString('utf8');
  throw new Error('No base64 decoder available in this runtime');
}

/** Read claims without verifying. Only ever used client-side to check `exp`. */
export function decodeJwt(token: string): JwtClaims | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    const payload = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
    return JSON.parse(base64Decode(padded)) as JwtClaims;
  } catch {
    return null;
  }
}

/**
 * True when the token should be swapped before making a request. A missing or
 * unparseable token also returns true so the caller takes the refresh path
 * rather than firing a request that is certain to 401.
 */
export function shouldRefresh(
  token: string | null,
  nowMs: number = Date.now(),
  skewSeconds: number = REFRESH_SKEW_SECONDS,
): boolean {
  if (!token) return true;
  const claims = decodeJwt(token);
  if (!claims?.exp) return true;
  return claims.exp - Math.floor(nowMs / 1000) <= skewSeconds;
}

export function isExpired(token: string | null, nowMs: number = Date.now()): boolean {
  return shouldRefresh(token, nowMs, 0);
}

/** Error code the API returns on an expired access token, triggering one retry. */
export const TOKEN_EXPIRED_CODE = 'token_expired';
